import React from 'react';
import styled from '@emotion/styled';
import defaultTo from 'lodash/defaultTo';
import Tooltip from '@atlaskit/tooltip';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import { Text, Flex } from '@atlaskit/primitives';
import { ROVO_PRODUCT_ID } from '@css-xp/ppc-constants';
import {
  isEnterpriseInstanceOffering,
  isExistingStandaloneJPDProduct,
  isProductBelongsToJiraFamily,
} from '@css-xp/ppc-utils';
import { EntitlementItemV2 } from './entitlement-item-v2';
import { EntitlementItemType, ExternalProductsList, SelectType } from '../types';
import { AddNewProductsWrapper } from '../../../add-new-products';
import './entitlement-container.scss';
import { InactiveReasonStrings } from '../store-utils';
import groupBy from 'lodash/groupBy';
import { useOrgProductInstancesMap } from '../../../../store';
import { CartProductSchema } from '../../../../types';

const EntitlementContainerStyles = styled.div`
  .entitlement-container-item {
    padding: 8px !important;
    &.disabled {
      opacity: 1 !important;
    }
  }
`;

export const ExternalMessageWrapper = styled.div`
  padding: 8px;
`;

interface Props {
  list: EntitlementItemType[];
  inactiveList: EntitlementItemType[];
  onChange: (id: string, status: boolean) => void;
  isSelectionDisabled?: boolean;
  cloudSiteHost?: string;
  isAddNewProductBtnEnabled?: boolean;
  shouldShowAppsCount?: boolean;
  shouldShowTrialStatus?: boolean;
  showInactiveProducts?: boolean;
  shouldShowAddons?: boolean;
  shouldShowSandboxStatus?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  checkedItemMap?: Record<string, SelectType>;
  alreadyCheckedItemMap?: Record<string, SelectType>;
  version?: number;
  showExternalListMessage?: boolean;
  externalProductsList?: ExternalProductsList;
  isDefaultExpanded?: boolean;
  isSingleProductSelection?: boolean;
  isTWCPhase2Enabled?: boolean;
}

const sortSidebarItems = (a: EntitlementItemType, b: EntitlementItemType) => {
  const firstName = defaultTo(a?.name, a?.name ?? '');
  const secondName = defaultTo(b?.name, b?.name ?? '');
  return firstName.localeCompare(secondName);
};

export const isOrgInstance = (
  val: EntitlementItemType,
  orgProductInstancesMap: Record<string, CartProductSchema[]>
) => {
  /**
   * If the product is having parentEntitlementId and
   * parentEntitlementId is key of orgProductInstancesMap then it is an org instance.
   */
  return val.parentEntitlementId && Object.keys(orgProductInstancesMap).includes(val.parentEntitlementId);
};

export const getSortedList = (
  list: EntitlementItemType[],
  config?: { isJPDAsJiraFamilyEnabled?: boolean },
  orgProductInstancesMap?: Record<string, CartProductSchema[]>,
  isTWCPhase2Enabled?: boolean
) => {
  const { isJPDAsJiraFamilyEnabled } = config ?? {};
  const allJiraProducts = list?.filter(
    (product) =>
      isProductBelongsToJiraFamily(product.productId, {
        isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(product.offeringId),
      }) && !(isTWCPhase2Enabled && isOrgInstance(product, orgProductInstancesMap ?? {}))
  );
  const sortedJiraProducts = allJiraProducts?.sort(sortSidebarItems) ?? [];
  const lastJiraProduct = sortedJiraProducts?.[sortedJiraProducts?.length - 1];
  const nonJiraProducts = list?.filter(
    (product) =>
      !isProductBelongsToJiraFamily(product.productId, {
        isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(product.offeringId),
      }) && !(isTWCPhase2Enabled && isOrgInstance(product, orgProductInstancesMap ?? {}))
  );
  const sortedNonJiraProducts = nonJiraProducts?.sort(sortSidebarItems) ?? [];
  return { products: [...sortedNonJiraProducts, ...sortedJiraProducts], lastJiraProduct };
};

/**
 * we need to show integrated apps count for last jira item. Because apps are linked to jira families container id.
 * if item is not jira family then we are directly consider to show the apps count.
 */
export const showIntegratedApps = ({
  entitlement,
  lastJiraProduct,
  shouldShowAppsCount,
  isJPDAsJiraFamilyEnabled = false,
}: {
  entitlement: EntitlementItemType;
  lastJiraProduct: EntitlementItemType;
  shouldShowAppsCount: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
}) => {
  if (!shouldShowAppsCount) return false;

  // we need to show integrated apps count for last jira item
  if (
    isProductBelongsToJiraFamily(entitlement.productId, {
      isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled && !isExistingStandaloneJPDProduct(entitlement.offeringId),
    })
  ) {
    return entitlement.id === lastJiraProduct?.id;
  }
  // we are directly consider to show the apps count for non jira families
  return true;
};

export const EntitlementContainer = ({
  list,
  inactiveList,
  onChange,
  isSelectionDisabled = false,
  cloudSiteHost,
  isAddNewProductBtnEnabled = false,
  shouldShowAppsCount = false,
  shouldShowTrialStatus = false,
  showInactiveProducts = false,
  shouldShowAddons = false,
  shouldShowSandboxStatus = false,
  isJPDAsJiraFamilyEnabled = false,
  checkedItemMap,
  alreadyCheckedItemMap,
  version,
  showExternalListMessage = false,
  externalProductsList,
  isDefaultExpanded,
  isSingleProductSelection,
  isTWCPhase2Enabled,
}: Props) => {
  const orgProductInstancesMap = useOrgProductInstancesMap();
  /**
   * we need to show integrated apps count for last jira item. Because apps are linked to jira families container id.
   * here we are filtering all jira families to get last index jira item.
   */
  const { products: allProductsSorted = [], lastJiraProduct } = getSortedList(
    list,
    { isJPDAsJiraFamilyEnabled },
    orgProductInstancesMap,
    isTWCPhase2Enabled
  );
  const {
    products: allInactiveProductsSorted = [],
    lastJiraProduct: lastJiraProductForInactive,
  } = getSortedList(inactiveList, { isJPDAsJiraFamilyEnabled });
  const EntitlementContainerWrapper = version === 2 ? EntitlementContainerStyles : React.Fragment;

  let isExternalEntitlementsPendingFetch = false;
  if (cloudSiteHost) {
    const alreadyFetchedIds: Record<string, boolean> = list?.reduce((acc: Record<string, boolean>, entitlement) => {
      acc[entitlement.id] = true;
      entitlement?.linkedEntitlements?.reduce((linkedEntitlementAcc, entitlement) => {
        linkedEntitlementAcc[entitlement.id] = true;
        return acc;
      }, acc);
      return acc;
    }, {});
    const externalProductListGroupedByCloudSiteId = groupBy(externalProductsList, 'cloudSiteHostname');
    const hasExternalProducts = !!externalProductListGroupedByCloudSiteId[cloudSiteHost]?.length;
    if (hasExternalProducts) {
      isExternalEntitlementsPendingFetch = externalProductListGroupedByCloudSiteId[cloudSiteHost].some(
        ({ id }) => !alreadyFetchedIds[id]
      );
    }
  }

  return (
    <div>
      {isAddNewProductBtnEnabled && (
        <div className="pt-8 ph-8">
          <AddNewProductsWrapper
            cloudSiteHost={cloudSiteHost ?? ''}
            addNewProductLabel={'New product/app'}
            addNewProductBtnAppearance={'link'}
            isDisabled={false}
          />
        </div>
      )}
      {allProductsSorted.map((val, index) => {
        if (!val?.isOrgProduct && val?.productId === ROVO_PRODUCT_ID) {
          return (
            <Tooltip content={'You can’t change this entitlement.'} tag="div" key={index}>
              <EntitlementContainerWrapper>
                <div
                  className="pv-8 entitlement-container-item disabled"
                  data-testId="entitlement-container-item-disabled"
                >
                  <EntitlementItemV2
                    key={index}
                    {...val}
                    onChange={() => {
                      /*empty method*/
                    }}
                    isDisabled={true}
                    shouldShowAppsCount={false}
                    shouldShowTrialStatus={shouldShowTrialStatus}
                    version={version}
                    isDefaultExpanded={isDefaultExpanded}
                    isSingleProductSelection={isSingleProductSelection}
                  />
                </div>
              </EntitlementContainerWrapper>
            </Tooltip>
          );
        }
        //disable the enterprise instance offering
        if (isEnterpriseInstanceOffering(val?.offeringName)) {
          return (
            <Tooltip content={'You can’t change this entitlement.'} tag="div" key={index}>
              <EntitlementContainerWrapper>
                <div
                  className="pv-8 entitlement-container-item disabled"
                  data-testId="entitlement-container-item-disabled"
                >
                  <EntitlementItemV2
                    key={index}
                    {...val}
                    onChange={() => {
                      /*empty method*/
                    }}
                    isDisabled={true}
                    shouldShowAppsCount={false}
                    shouldShowTrialStatus={shouldShowTrialStatus}
                    version={version}
                    isDefaultExpanded={isDefaultExpanded}
                    isSingleProductSelection={isSingleProductSelection}
                  />
                </div>
              </EntitlementContainerWrapper>
            </Tooltip>
          );
        }

        return (
          <EntitlementContainerWrapper key={index}>
            <div className="pv-8 entitlement-container-item" data-testId="entitlement-container-item" key={index}>
              <EntitlementItemV2
                key={index}
                {...val}
                onChange={onChange}
                isDisabled={isSelectionDisabled}
                shouldShowAppsCount={showIntegratedApps({
                  entitlement: val,
                  lastJiraProduct,
                  shouldShowAppsCount,
                  isJPDAsJiraFamilyEnabled,
                })}
                shouldShowTrialStatus={shouldShowTrialStatus}
                shouldShowAddons={shouldShowAddons}
                shouldShowSandboxStatus={shouldShowSandboxStatus}
                checkedItemMap={checkedItemMap}
                alreadyCheckedItemMap={alreadyCheckedItemMap}
                version={version}
                isDefaultExpanded={isDefaultExpanded}
                isSingleProductSelection={isSingleProductSelection}
                orgProductInstancesMap={orgProductInstancesMap}
                isTWCPhase2Enabled={isTWCPhase2Enabled}
              />
            </div>
          </EntitlementContainerWrapper>
        );
      })}
      {showInactiveProducts &&
        allInactiveProductsSorted.map((val, index) => {
          const hasAddons = val.addons?.length > 0;
          // FIXME :: move this logic to split in store-utils and use getInactiveReason method for this
          const disableReason = val.inactiveReason
            ? val.inactiveReason
            : hasAddons
            ? InactiveReasonStrings.ADDONS
            : val.isFutureDatedSubscription
            ? InactiveReasonStrings.FUTURE_DATED
            : InactiveReasonStrings.GENERIC;

          return (
            <Tooltip content={disableReason} tag="div" key={index}>
              <EntitlementContainerWrapper>
                <div
                  className="pv-8 entitlement-container-item disabled"
                  data-testId="entitlement-container-item-disabled"
                >
                  <EntitlementItemV2
                    key={index}
                    {...val}
                    onChange={() => {
                      /*empty method*/
                    }}
                    isDisabled={true}
                    shouldShowAppsCount={showIntegratedApps({
                      entitlement: val,
                      lastJiraProduct: lastJiraProductForInactive,
                      shouldShowAppsCount,
                      isJPDAsJiraFamilyEnabled,
                    })}
                    shouldShowAddons={shouldShowAddons}
                    shouldShowTrialStatus={shouldShowTrialStatus}
                    version={version}
                    isSingleProductSelection={isSingleProductSelection}
                  />
                </div>
              </EntitlementContainerWrapper>
            </Tooltip>
          );
        })}
      {showExternalListMessage && isExternalEntitlementsPendingFetch && (
        <ExternalMessageWrapper>
          <Flex alignItems="center" gap="space.300">
            <WarningIcon label="" primaryColor={token('color.icon.accent.orange')} />
            <Text size="small">
              Some subscriptions cannot be shown. An authorized customer billing admin email is needed to view them.
            </Text>
          </Flex>
        </ExternalMessageWrapper>
      )}
    </div>
  );
};
